import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Auth, authState } from '../../core/firebase/auth';
import { LicenseApiService } from '../../services/license-api.service';
import { SchoolApiService } from '../../services/school-api.service';
import { SchoolAuthorityApiService } from '../../services/school-authority-api.service';
import type { LicenseTypeEnum, OrderDetail } from '../../types/rest-api';

export type PendingOrderSetup = 'school' | 'authority';

/** Tab-scoped: the uid whose orders were already checked in this tab. */
const CHECKED_KEY = 'bn_pending_order_setup_checked';

/**
 * The organisation a paid order still waits for: an active plan order, but no
 * Schulgemeinde or Schuleinheit yet. Happens when the onboarding tab closed
 * between ordering and setup, or the user switched devices.
 */
export function pendingOrderSetup(
  orders: OrderDetail[],
  hasAuthority: boolean,
  hasSchool: boolean
): PendingOrderSetup | null {
  if (hasAuthority || hasSchool) return null;
  const active = new Set<LicenseTypeEnum | undefined>(
    orders.flatMap(o =>
      (o.items ?? [])
        .filter(it => !it.is_cancelled)
        .map(it => it.license?.license_type)
    )
  );
  if (active.has('SCHOOL_AUTHORITY_SUBSCRIPTION')) return 'authority';
  if (active.has('SCHOOL_UNIT_SUBSCRIPTION')) return 'school';
  return null;
}

/**
 * Stops {@link pendingOrderSetupGuard} for this user in this tab. Called by
 * the wizards in resume mode, so leaving them does not send the user back.
 */
export function markPendingOrderSetupSeen(uid: string | undefined): void {
  if (uid) sessionStorage.setItem(CHECKED_KEY, uid);
}

/**
 * Sends a user whose paid order has no organisation back into onboarding to
 * finish it. Once per tab (see {@link markPendingOrderSetupSeen}), and lets
 * the user through whenever a lookup fails.
 */
export const pendingOrderSetupGuard: CanActivateFn = async (): Promise<
  boolean | UrlTree
> => {
  const router = inject(Router);
  const auth = inject(Auth);
  const licenseApi = inject(LicenseApiService);
  const schoolApi = inject(SchoolApiService);
  const schoolAuthorityApi = inject(SchoolAuthorityApiService);

  try {
    const user = await firstValueFrom(authState(auth));
    if (!user || sessionStorage.getItem(CHECKED_KEY) === user.uid) return true;

    const orders = await firstValueFrom(licenseApi.getOrders());
    let pending = pendingOrderSetup(orders, false, false);
    if (pending) {
      const [authorities, schools] = await Promise.all([
        firstValueFrom(schoolAuthorityApi.getMySchoolAuthorities()),
        firstValueFrom(schoolApi.getMySchools()),
      ]);
      pending = pendingOrderSetup(
        orders,
        authorities.length > 0,
        schools.length > 0
      );
    }
    // A redirect only counts once the wizard shows (see
    // markPendingOrderSetupSeen): login navigates to `/` twice, and the second
    // navigation can cancel this redirect.
    if (!pending) markPendingOrderSetupSeen(user.uid);

    if (pending === 'school') {
      return router.createUrlTree(['/onboarding/school'], {
        queryParams: { resumeAfterOrder: '1' },
      });
    }
    if (pending === 'authority') {
      return router.createUrlTree(['/onboarding/municipality'], {
        queryParams: { resumeAfterOrder: '1' },
      });
    }
    return true;
  } catch (e) {
    console.warn('Pending order setup check failed:', e);
    return true;
  }
};
